// Notices a newer deploy while a tab is open and applies it without getting in anyone's way.
// Each build writes /version.json with its id (see apps/*/vite.config.ts) and bakes the same id
// into the bundle as VITE_BUILD_ID. When the two differ, a newer (or rolled back) site is live.
import { createElement, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button } from './Button.tsx';

/** How an app may apply a new build: admin on the next page change, kitchen when idle, order away from checkout. */
export type UpdatePolicy = 'admin' | 'kitchen' | 'order';
export type UpdateAction = 'none' | 'show-bar' | 'reload';

/** How often an open tab asks the site which build is live. */
export const UPDATE_CHECK_MS = 60_000;
/**
 * Automatic reloads never happen twice inside this window. Right after a deploy the site can
 * briefly serve old and new files side by side; without this a tab could reload over and over.
 */
export const AUTO_RELOAD_GAP_MS = 120_000;
const RELOAD_KEY = 'crave-app-update-auto-reload-at';
const PAYMENT_PARAMS = ['payment_intent', 'setup_intent', 'redirect_status', 'confirming'];

/** The build id inside a /version.json body, or null for anything else (an HTML page, a blank). */
export function readBuildVersion(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const version = (parsed as { version?: unknown }).version;
    if (typeof version !== 'string') return null;
    const id = version.trim();
    return id && id.length <= 100 ? id : null;
  } catch {
    return null;
  }
}

/** True when the site serves a different build than the one running in this tab. */
export function isNewBuild(
  running: string | null | undefined,
  served: string | null | undefined,
): boolean {
  const a = running?.trim();
  const b = served?.trim();
  return Boolean(a && b && a !== b);
}

/** Asks the site which build is live, skipping every cache. Null when it cannot tell. */
export async function fetchServedVersion(
  url = '/version.json',
  fetchImpl: (input: string, init: RequestInit) => Promise<Response> = (input, init) =>
    fetch(input, init),
): Promise<string | null> {
  try {
    const res = await fetchImpl(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return readBuildVersion(await res.text());
  } catch {
    return null;
  }
}

/** Cart, checkout, or coming back from a payment page: the order site never reloads here. */
export function isCheckoutPath(path: string, search = ''): boolean {
  if (/^\/(cart|checkout)(\/|$)/.test(path)) return true;
  const params = new URLSearchParams(search);
  return PAYMENT_PARAMS.some((p) => params.has(p));
}

export interface UpdateSituation {
  policy: UpdatePolicy;
  /** A different build is live. */
  updateReady: boolean;
  path: string;
  search?: string;
  /** The page just changed to `path` from another page. */
  navigated: boolean;
  /** A save or load is running, or someone is typing in a field. */
  busy: boolean;
}

/** What to do about a newer build right now. */
export function decideUpdate(s: UpdateSituation): UpdateAction {
  if (!s.updateReady) return 'none';
  // A set-password link works once; a reload could cost the person their link.
  if (s.path.startsWith('/reset-password')) return 'none';
  if (s.policy === 'kitchen') return s.busy ? 'show-bar' : 'reload';
  if (s.policy === 'admin') return s.navigated && !s.busy ? 'reload' : 'show-bar';
  if (isCheckoutPath(s.path, s.search)) return 'none';
  return s.navigated && s.path === '/' && !s.busy ? 'reload' : 'show-bar';
}

/** False when this tab already reloaded on its own a moment ago. */
export function autoReloadAllowed(lastAutoReloadAt: number | null, now: number): boolean {
  if (lastAutoReloadAt === null) return true;
  return now < lastAutoReloadAt || now - lastAutoReloadAt >= AUTO_RELOAD_GAP_MS;
}

function readLastAutoReload(): number | null {
  try {
    const n = Number(globalThis.sessionStorage?.getItem(RELOAD_KEY));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function markAutoReload(now: number): void {
  try {
    globalThis.sessionStorage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // Private browsing can refuse storage; the reload still happens.
  }
}

const TYPING_INPUTS = new Set([
  '',
  'text',
  'search',
  'email',
  'tel',
  'url',
  'password',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
]);

/** True while the focus is in a field someone types into. */
export function isTyping(doc: Pick<Document, 'activeElement'> | undefined = globalThis.document) {
  const el = doc?.activeElement as
    (Element & { isContentEditable?: boolean; type?: string }) | null | undefined;
  if (!el) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  return el.tagName === 'INPUT' && TYPING_INPUTS.has((el.type ?? '').toLowerCase());
}

/** The code file named in a failed page-load error, if any. */
export function missingCodeUrl(message: string): string | null {
  return /(?:https?:\/\/[^\s'"]+)?\/assets\/[^\s'"]+?\.(?:js|css)/.exec(message)?.[0] ?? null;
}

/**
 * A tab open across a deploy can ask for a code file the new deploy no longer has. Refresh the
 * browser's copy of that file (it may have saved the site's fallback page under that name) and
 * reload once. Returns a function that stops listening.
 */
export function reloadOnMissingCode(): () => void {
  const onError = (event: Event) => {
    const now = Date.now();
    if (!autoReloadAllowed(readLastAutoReload(), now)) return;
    event.preventDefault();
    markAutoReload(now);
    const payload = (event as Event & { payload?: unknown }).payload;
    const message =
      payload instanceof Error ? payload.message : typeof payload === 'string' ? payload : '';
    const url = missingCodeUrl(message);
    const refresh = url
      ? fetch(url, { cache: 'reload' }).then(
          () => undefined,
          () => undefined,
        )
      : Promise.resolve();
    void refresh.finally(() => window.location.reload());
  };
  window.addEventListener('vite:preloadError', onError);
  return () => window.removeEventListener('vite:preloadError', onError);
}

export interface AppUpdateOptions {
  policy: UpdatePolicy;
  /** The build id baked into this bundle (VITE_BUILD_ID). */
  currentVersion: string | undefined;
  /** The current page path and query, from the router. */
  path: string;
  search?: string;
  /** True while an automatic reload would interrupt something. */
  isBusy?: () => boolean;
  /** Off in development: there is no /version.json there. */
  enabled?: boolean;
  versionUrl?: string;
  intervalMs?: number;
}

/**
 * Checks /version.json every minute and whenever the tab comes back into view, then follows the
 * app's policy. Render <AppUpdateBar onReload={reload} /> when `action` is 'show-bar'.
 */
export function useAppUpdate(options: AppUpdateOptions): {
  action: 'none' | 'show-bar';
  reload: () => void;
} {
  const {
    policy,
    currentVersion,
    path,
    search = '',
    isBusy,
    enabled = true,
    versionUrl = '/version.json',
    intervalMs = UPDATE_CHECK_MS,
  } = options;
  const [served, setServed] = useState<string | null>(null);
  const [action, setAction] = useState<'none' | 'show-bar'>('none');
  const [tick, setTick] = useState(0);
  const lastPath = useRef(path);
  const busy = useRef(isBusy);
  useEffect(() => {
    busy.current = isBusy;
  });
  const updateReady = enabled && isNewBuild(currentVersion, served);

  useEffect(() => {
    if (!enabled || !currentVersion) return;
    let stopped = false;
    let checking = false;
    const check = async () => {
      if (stopped || checking) return;
      checking = true;
      try {
        const version = await fetchServedVersion(versionUrl);
        if (!stopped && version) setServed(version);
      } finally {
        checking = false;
      }
    };
    const whenVisible = () => {
      if (document.visibilityState !== 'hidden') void check();
    };
    void check();
    const timer = setInterval(whenVisible, intervalMs);
    document.addEventListener('visibilitychange', whenVisible);
    window.addEventListener('focus', whenVisible);
    window.addEventListener('online', whenVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', whenVisible);
      window.removeEventListener('focus', whenVisible);
      window.removeEventListener('online', whenVisible);
    };
  }, [enabled, currentVersion, versionUrl, intervalMs]);

  // The kitchen waits for a quiet moment: look again every two seconds once an update is ready.
  useEffect(() => {
    if (!updateReady || policy !== 'kitchen') return;
    const timer = setInterval(() => setTick((t) => t + 1), 2_000);
    return () => clearInterval(timer);
  }, [updateReady, policy]);

  useEffect(() => {
    const navigated = lastPath.current !== path;
    lastPath.current = path;
    const next = decideUpdate({
      policy,
      updateReady,
      path,
      search,
      navigated,
      busy: busy.current?.() ?? false,
    });
    const now = Date.now();
    if (next === 'reload' && autoReloadAllowed(readLastAutoReload(), now)) {
      markAutoReload(now);
      window.location.reload();
      return;
    }
    setAction(next === 'reload' ? 'show-bar' : next);
  }, [policy, updateReady, path, search, tick]);

  return { action, reload: () => window.location.reload() };
}

export interface AppUpdateBarProps {
  onReload: () => void;
  message?: string;
  /** The order menu keeps its cart button at the bottom, so the order site uses 'top'. */
  placement?: 'top' | 'bottom';
}

/** The slim "Crave was updated" bar with a Reload button. */
export function AppUpdateBar({
  onReload,
  message = 'Crave was updated.',
  placement = 'bottom',
}: AppUpdateBarProps) {
  const wrap: CSSProperties = {
    position: 'fixed',
    left: 0,
    right: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 12px',
    pointerEvents: 'none',
    ...(placement === 'top'
      ? { top: 'calc(8px + env(safe-area-inset-top, 0px))' }
      : { bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }),
  };
  const bar: CSSProperties = {
    pointerEvents: 'auto',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: 520,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 6px 6px 16px',
    background: 'var(--ink)',
    color: '#fff',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow)',
    fontFamily: 'var(--font-sans)',
    fontSize: 15,
    fontWeight: 600,
  };
  return createElement(
    'div',
    { style: wrap, role: 'status', 'aria-live': 'polite' },
    createElement(
      'div',
      { style: bar },
      createElement('span', { style: { flex: 1 } }, message),
      createElement(Button, { type: 'button', onClick: onReload, children: 'Reload' }),
    ),
  );
}
